import React from 'react';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { i18next } from '@frontend/i18n';
import { getAiOutcomeIconWithTooltip, getAiOutcomeIcon, OUTCOME_MAPPING } from '@frontend/phone-ui';

interface AiOutcomeIconProps {
  outcome?: AIOutcome;
  showTooltip?: boolean;
  className?: string;
  customTooltip?: string;
}

interface OutcomeConfig {
  mappedOutcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING];
  tooltip: string;
}

const AI_OUTCOME_CONFIG: Partial<Record<AIOutcome, OutcomeConfig>> = {
  [AIOutcome.AI_OUTCOME_END_CALL]: {
    mappedOutcome: OUTCOME_MAPPING.SUCCESS,
    tooltip: i18next.t('AI Receptionist successfully assisted the patient.', { ns: 'call-intelligence' }),
  },
  [AIOutcome.AI_OUTCOME_FATAL]: {
    mappedOutcome: OUTCOME_MAPPING.FATAL,
    tooltip: i18next.t('AI Receptionist failed to answer this call.', { ns: 'call-intelligence' }),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: {
    mappedOutcome: OUTCOME_MAPPING.ROUTE_MISSED,
    tooltip: i18next.t('Your office missed the transfer from the AI Receptionist. The caller may need assistance.', {
      ns: 'call-intelligence',
    }),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: {
    mappedOutcome: OUTCOME_MAPPING.ROUTE_ANSWERED,
    tooltip: i18next.t('AI Receptionist transferred the call to your office.', { ns: 'call-intelligence' }),
  },
  [AIOutcome.AI_OUTCOME_TEXT_HANDOFF]: {
    mappedOutcome: OUTCOME_MAPPING.TEXT_HANDOFF,
    tooltip: i18next.t('AI Receptionist continued the conversation over text.', { ns: 'call-intelligence' }),
  },
  [AIOutcome.AI_OUTCOME_INCOMPLETE]: {
    mappedOutcome: OUTCOME_MAPPING.INCOMPLETE,
    tooltip: i18next.t('Caller hung up before AI Receptionist could determine the outcome.', {
      ns: 'call-intelligence',
    }),
  },
} as const;

export const AiOutcomeIcon: React.FC<AiOutcomeIconProps> = ({
  outcome,
  showTooltip = false,
  className,
  customTooltip,
}) => {
  if (!outcome) return null;
  const config = AI_OUTCOME_CONFIG[outcome];

  if (!config) {
    return null;
  }

  const tooltip = customTooltip || config.tooltip;

  const IconComponent = showTooltip
    ? getAiOutcomeIconWithTooltip({
        outcome: config.mappedOutcome,
        tooltip,
      })
    : getAiOutcomeIcon({ outcome: config.mappedOutcome });

  if (!IconComponent) {
    return null;
  }

  return (
    <span className={className} data-testid={`ai-outcome-icon-${outcome}`}>
      {IconComponent}
    </span>
  );
};
